<template>
  <div class="newsticker" :style="{ height }">
    <div v-show="enabled" class="roller" ref="roller" :style="{ height }">
      <slot />
    </div>

    <slot v-if="!enabled" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { until, useElementSize, useMounted, useRafFn, watchImmediate } from '@vueuse/core'

const MAX_ITEM = 100
const props = defineProps({
  speed: {
    type: Number,
    default: 1,
  },
  enabled: {
    type: Boolean,
    default: true,
  },
})
const roller = ref(null)
const mounted = useMounted()
const cloning = ref(true)
const childrenHeights = ref([])
const height = computed(() => {
  if (!props.enabled) return 'auto'
  const heights = childrenHeights.value.map(ch => ch.value)
  return heights.length === 0 ? 'auto' : `${Math.max(...heights)}px`
})
let translateX = 0
let midIndex = 1

watchImmediate(
  () => mounted.value && props.enabled,
  on => on && pack(),
)

until(() => props.enabled && !cloning.value)
  .toBeTruthy()
  .then(roll)

function pack() {
  const { pause } = useRafFn(() => {
    cloning.value = !lastItemIsFarAway()
    cloning.value && clone()
  })

  until(cloning)
    .toBe(false)
    .then(() => {
      pause()
      childrenHeights.value = [...roller.value.children].map(useElementSize).map(size => size.height)
    })
}

function clone() {
  for (let node of [...roller.value.children]) {
    if (roller.value.children.length >= MAX_ITEM) {
      cloning.value = false
      return
    }

    roller.value.appendChild(node.cloneNode(true))
  }
}

function roll() {
  midIndex = Math.ceil(roller.value.children.length / 2)

  useRafFn(({ delta }) => {
    translateX -= props.speed * (delta / 1000)
    roller.value.style.transform = `translateX(${translateX}px)`

    const start = roller.value.children[0]
    const end = roller.value.children[midIndex]
    const startRect = start.getBoundingClientRect()
    const endRect = end.getBoundingClientRect()
    if (endRect.x < -endRect.width) {
      translateX += endRect.x - startRect.x
      ;[...roller.value.children].slice(0, midIndex).forEach(n => roller.value.appendChild(n))
    }
  })
}

function lastItemIsFarAway() {
  const items = [...roller.value.children]
  const screen = Math.max(window.innerWidth, 1920)
  return items.at(-1).getBoundingClientRect().x > screen * 2
}
</script>

<style lang="scss" scoped>
.newsticker {
  width: 100%;
  position: relative;
  overflow-x: hidden;

  .roller {
    position: absolute;
    left: 0;
    display: flex;
    align-items: center;
  }
}
</style>
